import {
  createContext,
  FC,
  PropsWithChildren,
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import { useParams } from 'react-router'
import { skipToken } from '@reduxjs/toolkit/query'

import { identify, track, trackPage } from './logging'
import {
  PostQuoteFeatureFlags,
  PreQuoteFeatureFlags,
  useTrackingFeatureFlags,
} from '../featureFlags'
import { LocalStorage } from 'directFlow/state/externalStore/utils'
import { useEditMode } from 'directFlow/utils'

import env from 'env'
import { ImpactSegmentContext, PriceAPIResponse, PriceV2ApiResponse, QuoteV2 } from 'types'
import { quotesApi } from 'app/services'
import { formatPrice, isKnownQuoteErrorObject, priceToPounds, Storage as storage } from 'utils'
import { directFlowToken } from 'utils/directFlowToken'
import { useSessionStorageTabIdTracking } from 'utils/hooks/useTabSession'

export const useImpactClickThrough = (
  quoteId?: string,
  quoteData?: QuoteV2,
): undefined | ImpactSegmentContext => {
  if (!quoteId) return undefined

  const localStorage = LocalStorage(quoteId)
  const localStorageState = localStorage.get()

  let impactClickId: null | string = null

  if (quoteData?.marketing.impactClickId) {
    impactClickId = quoteData?.marketing.impactClickId
  }

  if (!('errorType' in localStorageState) && localStorageState?.quoteGeneralInfo?.impactClickId) {
    impactClickId = localStorageState.quoteGeneralInfo.impactClickId
  }

  if (!impactClickId) return undefined

  return {
    context: {
      referrer: {
        type: 'impactRadius',
        id: impactClickId,
      },
    },
  }
}

export const attentivePurchaseTracking = () => {
  window._qevents = window._qevents || []

  window._qevents.push({
    qacct: 'p-QrY8FJ_wsLgJH',
    labels: '_fp.event.Purchase',
    event: 'refresh',
  })
}

const useGASession = () => {
  const gaSession = storage.get('gaSession')

  if (gaSession === null) return undefined

  return gaSession
}

const AB_TEST_KEY = 'abTests'

const useAbTestsTrackingRef = (): RefObject<{
  [AB_TEST_KEY]: {
    preQuoteFeatureFlags: PreQuoteFeatureFlags
    postQuoteFeatureFlags: PostQuoteFeatureFlags | null
  }
} | null> => {
  const featureFlagContext = useTrackingFeatureFlags()

  const setRef = () => {
    if (featureFlagContext === null) {
      return null
    }

    const { preQuoteFeatureFlags, postQuoteFeatureFlags, isPostQuoteFlagsDefault } =
      featureFlagContext

    return {
      [AB_TEST_KEY]: {
        preQuoteFeatureFlags: preQuoteFeatureFlags,
        postQuoteFeatureFlags: isPostQuoteFlagsDefault ? null : postQuoteFeatureFlags,
      },
    }
  }

  const abTestsRef = useRef(setRef())

  return abTestsRef
}

const BASKET_PROGRESS_KEY = 'basketProgress'
const QUOTE_DETAILS_KEY = 'quoteDetails'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SegmentQuoteData = Record<string, any>

export const useQuoteTrackingData = () => {
  const { quoteId } = useParams<{ quoteId?: string }>()

  const { getAddons, getQuoteV2, getPriceV2 } = quotesApi.endpoints
  const quoteState = getQuoteV2.useQueryState(quoteId ? { quoteId } : skipToken)
  const priceV2State = getPriceV2.useQueryState(quoteId ?? skipToken)
  const addonsState = getAddons.useQueryState(quoteId ?? skipToken)

  const quoteTrackingDataRef = useRef<SegmentQuoteData>({})

  if (quoteState.isSuccess) {
    quoteTrackingDataRef.current[QUOTE_DETAILS_KEY] = {
      vehicleType: quoteState.data.vehicle.vehicleType,
      channel: quoteState.data.channel.name,
      removeStepsOnDirect: quoteState.data.removeStepsOnDirect,
    }
  }

  if (quoteState.isSuccess && priceV2State.isSuccess) {
    const productPlan = quoteState.data.productPlan

    quoteTrackingDataRef.current[BASKET_PROGRESS_KEY] = {
      productPlan,
      price: {
        currency: priceV2State.data.currency,
        basketTotal: {
          annual: priceV2State.data?.prices[productPlan]?.annual.total.perYear,
          annualPounds: priceToPounds(
            priceV2State.data?.prices[productPlan]?.annual.total.perYear || 0,
          ),
          monthly: {
            firstMonth: priceV2State.data?.prices[productPlan]?.monthly.total.upfront,
            instalment: priceV2State.data?.prices[productPlan]?.monthly.total.perMonth,
          },
        },
      },
    }

    if (
      priceV2State.data.prices[productPlan]?.annual.total.studentRepriceChange ||
      priceV2State.data.prices[productPlan]?.monthly.total.studentRepriceChange
    ) {
      quoteTrackingDataRef.current[BASKET_PROGRESS_KEY].price = {
        ...quoteTrackingDataRef.current[BASKET_PROGRESS_KEY].price,
        studentPriceChange: {
          annual: priceV2State.data.prices[productPlan]?.annual.total.studentRepriceChange?.perYear,
          monthly:
            priceV2State.data.prices[productPlan]?.monthly.total.studentRepriceChange?.perMonth,
        },
      }
    }

    if (addonsState.isSuccess && addonsState.data[productPlan]?.availableAddons) {
      const { availableAddons } = addonsState.data[productPlan]
      const { selectedAddons } = addonsState.data
      const addonIds = Object.keys(availableAddons)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const addOns: Record<string, any> = {}

      addonIds.forEach((addonId) => {
        const selectedAddon = selectedAddons.find(
          (selectedAddon) => selectedAddon.addOnId === addonId,
        )
        const isSelected = selectedAddon !== undefined

        addOns[addonId] = isSelected
        addOns[`${addonId}_OPTION_ID`] = selectedAddon ? selectedAddon.optionId : undefined
      })

      quoteTrackingDataRef.current[BASKET_PROGRESS_KEY].addOns = addOns
    }
  }

  return quoteTrackingDataRef
}

const useQuoteIdTracking = () => {
  const { quoteId } = useParams<{ quoteId?: string }>()

  const { getQuoteV2 } = quotesApi.endpoints
  const quoteV2State = getQuoteV2.useQueryState(quoteId ? { quoteId: quoteId } : skipToken)

  const firstQuoteId = isKnownQuoteErrorObject(quoteV2State.error)
    ? quoteV2State.error.data.payload?.firstQuoteId
    : quoteV2State.currentData?.relatedQuotes.first

  return {
    quoteId,
    firstQuoteId: firstQuoteId,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Properties = Record<string, any>
export type TrackingFunction = (name: string, properties?: Properties) => void

type PageValue = string | null

const TrackingContext = createContext<{
  updatePage: (page: PageValue) => void
  pageRef: RefObject<PageValue>
} | null>(null)

const useTrackingContext = () => {
  const value = useContext(TrackingContext)
  if (value === null) {
    throw new Error(
      '`useTrackingContext` should always have `TrackingProvider` as a parent component',
    )
  }

  return value
}

export const TrackingProvider: FC<PropsWithChildren> = ({ children }) => {
  const pageRef = useRef<PageValue>(null)
  const updatePage = (page: PageValue) => {
    pageRef.current = page
  }

  return (
    <TrackingContext.Provider value={{ pageRef, updatePage }}>{children}</TrackingContext.Provider>
  )
}

export const useEventTracking = () => {
  const { quoteId } = useParams<{ quoteId?: string }>()

  const { pageRef } = useTrackingContext()
  const abTestsRef = useAbTestsTrackingRef()
  const basketProgressRef = useQuoteTrackingData()
  const impactContext = useImpactClickThrough(quoteId)
  const quoteIdTracking = useQuoteIdTracking()
  const editMode = useEditMode()
  const editProperties = { editMode }
  const gaSession = useGASession()
  const browserTabId = useSessionStorageTabIdTracking()

  return (message: string, properties?: Properties) => {
    track(
      message,
      {
        page: pageRef.current,
        ...quoteIdTracking,
        ...abTestsRef.current,
        ...basketProgressRef.current,
        ...properties,
        ...editProperties,
        ...gaSession,
        browserTabId,
        directFlowToken,
      },
      impactContext && { ...impactContext },
    )
  }
}

/**
 * Send a page event to Segment, this hook also return the tracking event utility
 * @returns track event function
 */
export const useTracking = (
  /**
   * `pageName` will be use for the page event
   * and will be passed as a property to all event inside this page
   */
  pageName: string,
  options?: Properties | { isFetched: boolean; properties: Properties },
): TrackingFunction => {
  const { quoteId } = useParams<{ quoteId?: string }>()

  const { updatePage } = useTrackingContext()
  const hasBeenSentRef = useRef(false)
  const track = useEventTracking()
  const abTestsRef = useAbTestsTrackingRef()
  const impactContext = useImpactClickThrough(quoteId)
  const basketProgressRef = useQuoteTrackingData()
  const quoteIdTracking = useQuoteIdTracking()
  const isProperties = options === undefined || !('isFetched' in options)
  const isFetched = isProperties ? true : options.isFetched
  const properties = isProperties ? options : (options.properties as Properties)
  const editMode = useEditMode()
  const editProperties = { editMode }
  const gaSession = useGASession()
  const browserTabId = useSessionStorageTabIdTracking()
  const ttclid = storage.get('ttclid')

  useLayoutEffect(() => {
    updatePage(pageName)
    return () => updatePage(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isFetched || hasBeenSentRef.current) {
      return
    }

    hasBeenSentRef.current = true
    trackPage(
      pageName,
      {
        page: pageName,
        ...quoteIdTracking,
        ...abTestsRef.current,
        ...basketProgressRef.current,
        ...properties,
        ...editProperties,
        ...gaSession,
        ...(ttclid && { ttclid }),
        browserTabId,
        directFlowToken,
      },
      impactContext && { ...impactContext },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched])

  return track
}

/**
 * Global utility function to initialise all traits of the current user that enter post quote flow.
 * These are also exposed to Braze.
 */
export const initAnalyticsPostQuoteUser = ({
  quoteId,
  priceData,
  priceV2Data,
  quoteV2Data,
  resumeSource,
}: {
  quoteId: string
  priceData?: PriceAPIResponse
  priceV2Data?: PriceV2ApiResponse
  quoteV2Data: QuoteV2
  resumeSource: string
}) => {
  if (!priceData && !priceV2Data) {
    throw new Error('initAnalyticsPostQuoteUser called without priceData or priceV2Data')
  }

  const annual =
    priceV2Data?.prices[quoteV2Data.productPlan]?.annual.total.perYear ||
    priceData?.totalPrice.annual.upFrontTotal.amountAsFixedPoint ||
    0

  const monthly =
    priceV2Data?.prices[quoteV2Data.productPlan]?.monthly.total.perMonth ||
    priceData?.totalPrice.monthly.monthlyTotal.amountAsFixedPoint ||
    0

  const resumeToken = priceV2Data?.resumeToken || priceData?.resumeToken

  const { policyHolder } = quoteV2Data

  // resumeSource tells the backend to direct resumes from the email link to the email landing page instead of the direct landing page
  const resumeQuoteLink = `${env.hostname}/quoting/quotes/${quoteId}/resume/email?resumeToken=${resumeToken}&resumeSource=email`

  identify(
    quoteId,
    {
      firstName: policyHolder.name.firstName,
      lastName: policyHolder.name.lastName,
      phoneNumber: policyHolder.phoneNumber,
      email: policyHolder.email,
      resumeQuoteLink,

      // Following properties should be considered deprecated as a Segment profile can have multiple quotes now and using the custom attributes on the profile will only show the latest quote
      // Track event properties are preferable
      annualPrice: formatPrice(annual),
      monthlyPrice: formatPrice(monthly),
      quoteId,
    },
    undefined,
  )

  track('LandingResumeLink', {
    resumeQuoteLink,
    channel: quoteV2Data.channel,
    annualPrice: formatPrice(annual),
    monthlyPrice: formatPrice(monthly),
    resumeSource,
    inceptionDate: quoteV2Data.inceptionDate,
    productPlan: quoteV2Data.productPlan,
  })
}
